import React, { useState, useEffect } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { localizationApi } from '../services/api';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { ScrollArea } from './ui/scroll-area';

const LANGUAGE_FLAGS = {
  'en': '🇺🇸',
  'zh-CN': '🇨🇳',
  'zh-TW': '🇹🇼',
  'zh-HK': '🇭🇰',
  'ja': '🇯🇵',
  'ko': '🇰🇷',
  'es': '🇪🇸',
  'fr': '🇫🇷',
  'de': '🇩🇪',
  'ar': '🇸🇦',
  'hi': '🇮🇳',
  'vi': '🇻🇳',
  'th': '🇹🇭'
};

const LanguageCurrencySelector = ({ user, onLocaleChange }) => {
  const [languages, setLanguages] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState({ code: 'en', name: 'English', native: 'English' });
  const [selectedCurrency, setSelectedCurrency] = useState({ code: 'USD', symbol: '$', name: 'US Dollar' });
  const [showDetectionModal, setShowDetectionModal] = useState(false);
  const [detectedLocation, setDetectedLocation] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLocalizationData();
  }, []);

  const loadLocalizationData = async () => {
    try {
      // Load languages and currencies
      const [langData, currData] = await Promise.all([
        localizationApi.getLanguages(),
        localizationApi.getCurrencies()
      ]);

      setLanguages(langData.languages || []);
      setCurrencies(currData.currencies || []);

      // Check localStorage for saved preferences
      const savedLang = localStorage.getItem('medicare_language');
      const savedCurrency = localStorage.getItem('medicare_currency');

      if (savedLang) {
        const lang = langData.languages.find(l => l.code === savedLang);
        if (lang) setSelectedLanguage(lang);
      }

      if (savedCurrency) {
        const curr = currData.currencies.find(c => c.code === savedCurrency);
        if (curr) setSelectedCurrency(curr);
      }

      // If no saved preferences, detect location
      if (!savedLang && !savedCurrency) {
        detectLocation();
      }
    } catch (error) {
      console.error('Failed to load localization data:', error);
    } finally {
      setLoading(false);
    }
  };

  const detectLocation = async () => {
    try {
      const detection = await localizationApi.detect();
      if (detection.detected && detection.show_language_prompt) {
        setDetectedLocation(detection);
        setShowDetectionModal(true);
      }
    } catch (error) {
      console.error('Location detection failed:', error);
    }
  };

  const handleLanguageChange = (language) => {
    setSelectedLanguage(language);
    localStorage.setItem('medicare_language', language.code);
    
    // Update user preferences if logged in
    if (user?.id) {
      localizationApi.updateUserPreferences(user.id, { language: language.code });
    }

    // Notify parent component
    onLocaleChange?.({ language: language.code, currency: selectedCurrency.code });
  };

  const handleCurrencyChange = (currency) => {
    setSelectedCurrency(currency);
    localStorage.setItem('medicare_currency', currency.code);
    
    if (user?.id) {
      localizationApi.updateUserPreferences(user.id, { currency: currency.code });
    }

    onLocaleChange?.({ language: selectedLanguage.code, currency: currency.code });
  };

  const applyDetectedSettings = () => {
    if (detectedLocation) {
      const lang = languages.find(l => l.code === detectedLocation.suggested_language);
      const curr = currencies.find(c => c.code === detectedLocation.suggested_currency);

      if (lang) handleLanguageChange(lang);
      if (curr) handleCurrencyChange(curr);
    }
    setShowDetectionModal(false);
  };

  const dismissDetection = () => {
    // Keep English and USD
    localStorage.setItem('medicare_language', 'en');
    localStorage.setItem('medicare_currency', 'USD');
    setShowDetectionModal(false);
  };

  if (loading) return null;

  return (
    <>
      {/* Dropdown Trigger */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-1.5 h-9">
            <Globe className="h-4 w-4" />
            <span className="hidden sm:inline text-sm">
              {LANGUAGE_FLAGS[selectedLanguage.code] || '🌐'} {selectedCurrency.code}
            </span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {/* Language Section */}
          <div className="px-2 py-1.5">
            <p className="text-xs font-medium text-muted-foreground mb-2">Language</p>
            <ScrollArea className="max-h-40">
              {languages.slice(0, 6).map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang)}
                  className="flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <span>{LANGUAGE_FLAGS[lang.code] || '🌐'}</span>
                    <span>{lang.native}</span>
                  </span>
                  {selectedLanguage.code === lang.code && <Check className="h-4 w-4" />}
                </DropdownMenuItem>
              ))}
            </ScrollArea>
          </div>
          
          <DropdownMenuSeparator />
          
          {/* Currency Section */}
          <div className="px-2 py-1.5">
            <p className="text-xs font-medium text-muted-foreground mb-2">Currency</p>
            <ScrollArea className="max-h-40">
              {currencies.slice(0, 8).map((curr) => (
                <DropdownMenuItem
                  key={curr.code}
                  onClick={() => handleCurrencyChange(curr)}
                  className="flex items-center justify-between"
                >
                  <span>{curr.symbol} {curr.code} - {curr.name}</span>
                  {selectedCurrency.code === curr.code && <Check className="h-4 w-4" />}
                </DropdownMenuItem>
              ))}
            </ScrollArea>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Detection Modal */}
      <Dialog open={showDetectionModal} onOpenChange={setShowDetectionModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-center">Welcome! 🌍</DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            {detectedLocation && (
              <>
                <p className="text-lg mb-4">
                  We detected you're visiting from <strong>{detectedLocation.country_name}</strong>
                </p>
                <div className="bg-muted rounded-lg p-4 mb-4">
                  <p className="text-sm text-muted-foreground mb-2">Would you like to browse in:</p>
                  <div className="flex items-center justify-center gap-4 text-lg">
                    <span className="flex items-center gap-2">
                      {LANGUAGE_FLAGS[detectedLocation.suggested_language] || '🌐'}
                      <span>{detectedLocation.language_info?.native || 'English'}</span>
                    </span>
                    <span className="text-muted-foreground">+</span>
                    <span className="font-medium">
                      {detectedLocation.currency_symbol} {detectedLocation.suggested_currency}
                    </span>
                  </div>
                </div>
                <div className="flex gap-3 justify-center">
                  <Button variant="outline" onClick={dismissDetection}>
                    Keep English (USD)
                  </Button>
                  <Button onClick={applyDetectedSettings}>
                    Yes, switch
                  </Button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LanguageCurrencySelector;
